import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { getUsers, getUser, createUser, updateUser, deleteUser } from '../api';
import { ROUTE_URLS } from '../routes';
import { getErrorMessage, getNotifyMessage, notify } from '../utils';

export const useUsers = (options = {}) => {
  return useQuery(
    'users',
    async () => {
      const res = await getUsers();
      return res.data;
    },
    {
      staleTime: Infinity,
      retry: false,
      ...options,
    },
  );
};

export const useUser = id => {
  return useQuery(
    ['user', id],
    async () => {
      const res = await getUser(id);
      return res.data;
    },
    {
      staleTime: Infinity,
      retry: false,
    },
  );
};

export const useCreateUser = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation(createUser, {
    onSuccess: () => {
      notify('success', getNotifyMessage('success', 'create', 'user'));
      queryClient.invalidateQueries();
      navigate(ROUTE_URLS.USERS);
    },
    onError: err => {
      notify('error', getErrorMessage(err));
    },
  });
};

export const useUpdateUser = ({ redirectTo = ROUTE_URLS.USERS, onSuccess } = {}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation(updateUser, {
    onSuccess: (res, variables) => {
      notify('success', getNotifyMessage('success', 'update', 'user'));
      onSuccess?.(variables);
      queryClient.invalidateQueries();
      navigate(redirectTo);
    },
    onError: err => {
      notify('error', getErrorMessage(err));
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteUser, {
    onSuccess: () => {
      notify('success', getNotifyMessage('success', 'delete', 'user'));
      queryClient.invalidateQueries();
    },
    onError: () => {
      notify('error', getNotifyMessage('error', 'delete', 'user'));
    },
  });
};
